import { ApiClient } from '@api/api-configuration';
import { BillingEntityTypeEnum, type BalanceItem, type BalancesSummaryResponse } from '@api/READ_ONLY/billing_api/Api';
import { ArrayOfAvailableSupportedCurrencies, DEFAULT_CURRENCY } from '@app/common/constants';
import { BILLING_QUERY_KEYS } from '@app/common/queryKeys';
import Text from '@app/components/ui/Text/Text';
import { APP_PREFIX, PAYMENTS_PAGE } from '@app/routes/urls';
import { containsNormalized } from '@app/utils/normalizeString';
import { currencyFormatter, type SupportedCurrencies } from '@app/utils/pricingCurrencyUtils';
import { useQuery } from '@tanstack/react-query';
import { Input } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IconType } from 'react-icons';
import { LuStethoscope } from 'react-icons/lu';
import { MdChevronRight, MdOutlineInfo, MdOutlinePerson, MdOutlineTipsAndUpdates, MdSearch } from 'react-icons/md';
import { RiNurseFill } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';

const BALANCE_QUERY_LIMIT = 500;
const SIDEBAR_SKELETON_ROWS = 3;
const SIDEBAR_SKELETON_IDS = ['sidebar-skeleton-a', 'sidebar-skeleton-b', 'sidebar-skeleton-c'] as const;
const SIDEBAR_SKELETON_ROW_IDS = ['sidebar-skeleton-row-a', 'sidebar-skeleton-row-b'] as const;

const hasNonZeroBalance = (item: BalanceItem) => (item.balance ?? 0) !== 0;

const resolveBalanceLabelKey = (
  entityType: BillingEntityTypeEnum,
  balance: number
): 'amount-due' | 'prepaid' | 'overpaid' | 'balance' => {
  if (balance > 0) {
    return 'amount-due';
  }

  if (entityType === BillingEntityTypeEnum.Client) {
    return 'prepaid';
  }

  if (balance < 0) {
    return 'overpaid';
  }

  return 'balance';
};

type SidebarSection = {
  entityType: BillingEntityTypeEnum;
  titleKey: 'clients' | 'doctors' | 'caregivers';
  total: number;
  rows: BalanceItem[];
};

type PaymentsSidebarProps = {
  activeEntityType?: BillingEntityTypeEnum;
  onEntitySelect?: (entityType: BillingEntityTypeEnum, entityId: number) => void;
};

const PaymentsSidebar = ({ activeEntityType, onEntitySelect }: PaymentsSidebarProps) => {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const [animatedRowKey, setAnimatedRowKey] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Only the party types get a sidebar section; caregiver_cash_register is a
  // special cash-register account handled by a separate feature, not a party.
  const entityConfig: Partial<
    Record<
      BillingEntityTypeEnum,
      {
        icon: IconType;
        color: string;
        titleKey: SidebarSection['titleKey'];
        surface: string;
        border: string;
        soft: string;
      }
    >
  > = {
    [BillingEntityTypeEnum.Client]: {
      icon: MdOutlinePerson,
      color: '#15803d',
      titleKey: 'clients',
      surface: 'linear-gradient(135deg, rgba(240,253,244,0.96), rgba(255,255,255,0.98))',
      border: 'rgba(134,239,172,0.9)',
      soft: 'rgba(220,252,231,0.95)',
    },
    [BillingEntityTypeEnum.Doctor]: {
      icon: LuStethoscope,
      color: '#475569',
      titleKey: 'doctors',
      surface: 'linear-gradient(135deg, rgba(248,250,252,0.96), rgba(255,255,255,0.98))',
      border: 'rgba(203,213,225,0.95)',
      soft: 'rgba(241,245,249,0.98)',
    },
    [BillingEntityTypeEnum.Caregiver]: {
      icon: RiNurseFill,
      color: '#0284c7',
      titleKey: 'caregivers',
      surface: 'linear-gradient(135deg, rgba(240,249,255,0.96), rgba(255,255,255,0.98))',
      border: 'rgba(125,211,252,0.95)',
      soft: 'rgba(224,242,254,0.98)',
    },
  };

  const balancesSummaryQuery = useQuery<BalancesSummaryResponse>({
    queryKey: [BILLING_QUERY_KEYS.BALANCES_SUMMARY],
    queryFn: async () => {
      const response = await ApiClient.billingApi.billing.getBalancesSummary();
      return response.data;
    },
  });

  const clientBalancesQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.BALANCES_LIST, BillingEntityTypeEnum.Client, 'payments-sidebar'],
    enabled: activeEntityType === BillingEntityTypeEnum.Client,
    queryFn: async () => {
      const response = await ApiClient.billingApi.billing.listBalances({
        type: BillingEntityTypeEnum.Client,
        sort: '-balance',
        offset: 0,
        limit: BALANCE_QUERY_LIMIT,
      });

      return response.data.data ?? [];
    },
  });

  const doctorBalancesQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.BALANCES_LIST, BillingEntityTypeEnum.Doctor, 'payments-sidebar'],
    enabled: activeEntityType === BillingEntityTypeEnum.Doctor,
    queryFn: async () => {
      const response = await ApiClient.billingApi.billing.listBalances({
        type: BillingEntityTypeEnum.Doctor,
        sort: '-balance',
        offset: 0,
        limit: BALANCE_QUERY_LIMIT,
      });

      return response.data.data ?? [];
    },
  });

  const caregiverBalancesQuery = useQuery({
    queryKey: [BILLING_QUERY_KEYS.BALANCES_LIST, BillingEntityTypeEnum.Caregiver, 'payments-sidebar'],
    enabled: activeEntityType === BillingEntityTypeEnum.Caregiver,
    queryFn: async () => {
      const response = await ApiClient.billingApi.billing.listBalances({
        type: BillingEntityTypeEnum.Caregiver,
        sort: '-balance',
        offset: 0,
        limit: BALANCE_QUERY_LIMIT,
      });

      return response.data.data ?? [];
    },
  });

  const responseCurrency = balancesSummaryQuery.data?.currency;
  const currency: SupportedCurrencies =
    responseCurrency && ArrayOfAvailableSupportedCurrencies.includes(responseCurrency as SupportedCurrencies)
      ? (responseCurrency as SupportedCurrencies)
      : DEFAULT_CURRENCY;
  const numberLocale = i18n.language;

  const activeSection = useMemo<SidebarSection | null>(() => {
    if (!activeEntityType) {
      return null;
    }

    if (activeEntityType === BillingEntityTypeEnum.Client) {
      return {
        entityType: BillingEntityTypeEnum.Client,
        titleKey: 'clients',
        total: balancesSummaryQuery.data?.totals?.clients ?? 0,
        rows: (clientBalancesQuery.data ?? []).filter(hasNonZeroBalance),
      };
    }

    if (activeEntityType === BillingEntityTypeEnum.Doctor) {
      return {
        entityType: BillingEntityTypeEnum.Doctor,
        titleKey: 'doctors',
        total: balancesSummaryQuery.data?.totals?.doctors ?? 0,
        rows: (doctorBalancesQuery.data ?? []).filter(hasNonZeroBalance),
      };
    }

    return {
      entityType: BillingEntityTypeEnum.Caregiver,
      titleKey: 'caregivers',
      total: balancesSummaryQuery.data?.totals?.caregivers ?? 0,
      rows: (caregiverBalancesQuery.data ?? []).filter(hasNonZeroBalance),
    };
  }, [
    activeEntityType,
    balancesSummaryQuery.data?.totals?.caregivers,
    balancesSummaryQuery.data?.totals?.clients,
    balancesSummaryQuery.data?.totals?.doctors,
    caregiverBalancesQuery.data,
    clientBalancesQuery.data,
    doctorBalancesQuery.data,
  ]);

  const activeBalancesQuery =
    activeEntityType === BillingEntityTypeEnum.Client
      ? clientBalancesQuery
      : activeEntityType === BillingEntityTypeEnum.Doctor
        ? doctorBalancesQuery
        : caregiverBalancesQuery;

  const isAnyLoading = Boolean(activeEntityType) && (balancesSummaryQuery.isLoading || activeBalancesQuery.isLoading);
  const hasError = Boolean(activeEntityType) && (balancesSummaryQuery.isError || activeBalancesQuery.isError);
  const filteredRows = useMemo(() => {
    if (!activeSection) {
      return [];
    }

    const trimmedSearchTerm = searchTerm.trim();

    if (!trimmedSearchTerm) {
      return activeSection.rows;
    }

    return activeSection.rows.filter((item) => containsNormalized(item.entityName ?? '', trimmedSearchTerm));
  }, [activeSection, searchTerm]);

  useEffect(() => {
    if (!animatedRowKey) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setAnimatedRowKey(null);
    }, 900);

    return () => window.clearTimeout(timeoutId);
  }, [animatedRowKey]);

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-hidden rounded-[20px] border border-slate-200 bg-[linear-gradient(145deg,#ffffff,rgba(248,250,252,0.96))] p-4 shadow-[0_14px_36px_rgba(15,23,42,0.05)]">
        <div className="flex items-start gap-2.5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-100 text-sky-600 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
            <MdOutlineTipsAndUpdates className="text-xl" />
          </div>
          <div className="flex min-w-0 flex-col gap-0.5">
            <Text type="subtitle2" transKey="tip" fw={700} className="text-slate-800" />
            <Text type="body2" transKey="payments-tip-description" color="#64748b" className="leading-5" />
          </div>
        </div>
      </div>

      <div className="flex max-h-[calc(100vh-220px)] overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-[0_18px_48px_rgba(15,23,42,0.08)]">
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="border-b border-slate-200 bg-[radial-gradient(circle_at_top_left,rgba(186,230,253,0.42),transparent_38%),linear-gradient(180deg,#ffffff,rgba(248,250,252,0.96))] px-4 py-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 max-w-[32rem]">
                <Text type="subtitle2" transKey="balances" className="text-slate-900" fw={700} />
                <Text
                  type="body2"
                  transKey="payments-balances-sidebar-description"
                  color="#64748b"
                  className="mt-1.5 block leading-5"
                />
              </div>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/90 text-slate-500 shadow-[0_8px_18px_rgba(15,23,42,0.08)] ring-1 ring-slate-200">
                <MdOutlineInfo className="text-lg" />
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-hidden p-4">
            <div className="space-y-3">
              {!activeEntityType && (
                <div className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50/70 px-4 py-5">
                  <Text
                    type="body2"
                    transKey="payments-balances-sidebar-select-staff-type"
                    color="#64748b"
                    className="leading-5"
                  />
                </div>
              )}

              {isAnyLoading &&
                SIDEBAR_SKELETON_IDS.slice(0, SIDEBAR_SKELETON_ROWS).map((skeletonId) => (
                  <div
                    key={skeletonId}
                    className="animate-pulse rounded-[20px] border border-slate-200 bg-[linear-gradient(145deg,#ffffff,rgba(248,250,252,0.94))] p-3.5"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="h-4 w-24 rounded bg-slate-200" />
                      <div className="h-4 w-20 rounded bg-slate-200" />
                    </div>
                    <div className="mt-3 space-y-2.5">
                      {SIDEBAR_SKELETON_ROW_IDS.map((rowId) => (
                        <div key={`${skeletonId}-${rowId}`} className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-slate-200" />
                          <div className="flex flex-1 flex-col gap-2">
                            <div className="h-3 w-28 rounded bg-slate-200" />
                            <div className="h-3 w-20 rounded bg-slate-200" />
                          </div>
                          <div className="h-4 w-16 rounded bg-slate-200" />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

              {hasError && <Text type="body2" transKey="payments-balances-sidebar-error" color="#ef4444" />}

              {!isAnyLoading &&
                !hasError &&
                activeSection &&
                (() => {
                  const section = activeSection;
                  const config = entityConfig[section.entityType];

                  // activeSection is only ever built for party types, so config
                  // is always present here; bail out defensively otherwise.
                  if (!config) {
                    return null;
                  }

                  const EntityIcon = config.icon;

                  return (
                    <div
                      key={section.entityType}
                      className="flex min-h-0 flex-col rounded-[20px] border p-3.5 shadow-[0_10px_28px_rgba(15,23,42,0.05)]"
                      style={{
                        background: config.surface,
                        borderColor: config.border,
                      }}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          <div
                            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white shadow-[inset_0_1px_0_rgba(255,255,255,0.9),0_6px_18px_rgba(15,23,42,0.06)]"
                            style={{ color: config.color, backgroundColor: config.soft }}
                          >
                            <EntityIcon size={18} />
                          </div>
                          <div className="min-w-0">
                            <Text type="subtitle2" transKey={section.titleKey} className="text-slate-900" />
                          </div>

                          <Input
                            allowClear
                            size="small"
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            placeholder={t('search')}
                            prefix={<MdSearch size={14} className="text-slate-400" />}
                            className="max-w-[160px]"
                            style={{ borderRadius: 12 }}
                          />
                        </div>

                        <div
                          className="rounded-xl border px-3 py-1.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.6)]"
                          style={{ borderColor: config.border, backgroundColor: 'rgba(255,255,255,0.72)' }}
                        >
                          <div className="flex items-center gap-1.5 whitespace-nowrap">
                            <Text type="caption" transKey="total" color="#64748b" />
                            <Text
                              type="body2"
                              untranslatedText={currencyFormatter(section.total, currency, numberLocale)}
                              className="text-slate-900"
                              fw={700}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 max-h-[calc(100vh-500px)] min-h-0 flex-1 space-y-2.5 overflow-y-auto pr-1">
                        {filteredRows.length > 0 ? (
                          filteredRows.map((item) => {
                            const rowKey = `${section.entityType}-${item.entityId}`;
                            const isAnimating = animatedRowKey === rowKey;
                            const balanceLabelKey = resolveBalanceLabelKey(section.entityType, item.balance ?? 0);

                            return (
                              <button
                                type="button"
                                key={rowKey}
                                onClick={() => {
                                  setAnimatedRowKey(rowKey);
                                  onEntitySelect?.(section.entityType, item.entityId);
                                  navigate(
                                    `/${APP_PREFIX}/${PAYMENTS_PAGE}?id=${item.entityId}&type=${section.entityType}`
                                  );
                                }}
                                className={[
                                  'group relative flex w-full items-center justify-between gap-2.5 overflow-hidden rounded-[18px] border px-3 py-2.5 text-left transition-all duration-200',
                                  isAnimating
                                    ? 'border-slate-200 bg-white shadow-[0_14px_30px_rgba(15,23,42,0.12)] scale-[1.01]'
                                    : 'border-white/80 bg-white/88 hover:-translate-y-0.5 hover:border-slate-200 hover:bg-white hover:shadow-[0_10px_22px_rgba(15,23,42,0.08)]',
                                ].join(' ')}
                                style={
                                  isAnimating
                                    ? {
                                        boxShadow: `0 14px 30px rgba(15,23,42,0.12), 0 0 0 1px ${config.border}`,
                                      }
                                    : undefined
                                }
                              >
                                <div
                                  className="absolute inset-y-2.5 left-0 w-1 rounded-full"
                                  style={{ backgroundColor: isAnimating ? config.color : 'transparent' }}
                                />

                                <div className="flex min-w-0 flex-1 items-center gap-2.5">
                                  <div
                                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
                                    style={{ backgroundColor: config.soft, color: config.color }}
                                  >
                                    <EntityIcon size={16} />
                                  </div>

                                  <div className="min-w-0 flex-1">
                                    <Text
                                      type="body2"
                                      untranslatedText={item.entityName}
                                      className="line-clamp-2 break-words text-slate-900"
                                      fw={600}
                                    />
                                  </div>
                                </div>

                                <div className="flex shrink-0 items-center gap-2">
                                  <div className="min-w-[112px] rounded-xl border border-slate-200 bg-white/90 px-2.5 py-2 text-right shadow-[inset_0_1px_0_rgba(255,255,255,0.7)]">
                                    <Text type="caption" transKey={balanceLabelKey} color="#64748b" className="block" />
                                    <Text
                                      type="body2"
                                      untranslatedText={currencyFormatter(
                                        Math.abs(item.balance ?? 0),
                                        currency,
                                        numberLocale
                                      )}
                                      color="#0f172a"
                                      fw={700}
                                      className="mt-1 block"
                                    />
                                  </div>

                                  <div
                                    className="flex h-8 w-8 items-center justify-center rounded-full border transition-transform duration-200 group-hover:translate-x-0.5"
                                    style={{
                                      borderColor: isAnimating ? config.border : 'rgba(226,232,240,0.95)',
                                      backgroundColor: isAnimating ? config.soft : 'rgba(248,250,252,0.95)',
                                      color: config.color,
                                    }}
                                  >
                                    <MdChevronRight size={16} />
                                  </div>
                                </div>
                              </button>
                            );
                          })
                        ) : (
                          <div className="rounded-[18px] border border-dashed border-slate-200 bg-white/80 px-4 py-4">
                            <Text type="body2" transKey="payments-balances-sidebar-empty" color="#64748b" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentsSidebar;
